import clsx from "clsx";
import type { JSX, ReactNode } from "react";
import Select, {
  type ClassNamesConfig,
  type GroupBase,
  type MultiValue,
} from "react-select";
import CreatableSelect from "react-select/creatable";
import type { ChoiceOption } from "./types";

export const FilterSection = ({
  title,
  children,
}: {
  title: string;
  description?: string;
  children: ReactNode;
}): JSX.Element => {
  return (
    <section className="space-y-3">
      <h3 className="text-[11px] font-bold uppercase tracking-wide text-(--text-muted)">
        {title}
      </h3>
      {children}
    </section>
  );
};

const FieldLabel = ({
  label,
}: {
  label: string;
  helperText?: string | undefined;
}): JSX.Element => (
  <span className="text-[12px] font-semibold text-(--text-secondary)">
    {label}
  </span>
);

const controlClassName = (isFocused: boolean): string =>
  clsx(
    "min-h-10 rounded-(--radius-md) border bg-(--surface-1) px-3 text-[13px] text-(--text-primary) transition",
    isFocused
      ? "border-(--brand-primary)"
      : "border-(--border-subtle) hover:border-(--border-strong)",
  );
const optionClassName = (isSelected: boolean, isFocused: boolean): string =>
  clsx(
    "cursor-pointer rounded-(--radius-md) px-3 py-2 text-[13px] transition",
    {
      "bg-(--pastel-violet) text-(--brand-primary) font-semibold": isSelected,
      "bg-(--surface-2) text-(--text-primary)": !isSelected && isFocused,
      "text-(--text-secondary)": !isSelected && !isFocused,
    },
  );
const dropdownIndicatorClassName = (isFocused: boolean): string =>
  clsx("px-1 text-(--text-secondary) transition", {
    "text-(--text-primary)": isFocused,
  });

// Shared by the pick-from-list and typed-value multi selects.
const multiSelectClassNames = <O,>(): ClassNamesConfig<
  O,
  true,
  GroupBase<O>
> => ({
  control: (state) => controlClassName(state.isFocused),
  valueContainer: () => "gap-1.5 py-1.5",
  placeholder: () => "text-[13px] text-(--text-muted)",
  input: () => "m-0 p-0 text-[13px] text-(--text-primary)",
  menu: () =>
    "mt-2 overflow-hidden rounded-(--radius-md) border border-(--border-subtle) bg-(--surface-1)",
  menuList: () => "max-h-[248px] p-1.5",
  option: (state) => optionClassName(state.isSelected, state.isFocused),
  multiValue: () => "rounded-(--radius-sm) bg-(--pastel-violet)",
  multiValueLabel: () =>
    "px-2 py-0.5 text-[12px] font-semibold text-(--brand-primary)",
  multiValueRemove: () =>
    "flex items-center rounded-r-(--radius-sm) px-1.5 text-(--brand-primary) transition hover:bg-(--brand-primary) hover:text-(--brand-primary-contrast)",
  indicatorsContainer: () => "gap-1",
  dropdownIndicator: (state) => dropdownIndicatorClassName(state.isFocused),
  clearIndicator: () =>
    "px-1 text-(--text-muted) transition hover:text-(--text-primary)",
  indicatorSeparator: () => "hidden",
  noOptionsMessage: () => "px-3 py-2 text-[13px] text-(--text-muted)",
});

export const MultiSelectField = <T extends string | number>({
  label,
  helperText,
  options,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  helperText?: string;
  options: ChoiceOption<T>[];
  value: T[];
  onChange: (nextValue: T[]) => void;
  placeholder?: string;
}): JSX.Element => {
  const selectedOptions = options.filter((option) =>
    value.includes(option.value),
  );
  return (
    <label className="block space-y-2">
      <FieldLabel label={label} helperText={helperText} />
      <Select<ChoiceOption<T>, true>
        isMulti
        isSearchable
        unstyled
        closeMenuOnSelect={false}
        hideSelectedOptions={false}
        backspaceRemovesValue={false}
        tabSelectsValue={false}
        options={options}
        value={selectedOptions}
        placeholder={placeholder ?? "Search and select..."}
        noOptionsMessage={() => "No matching options"}
        menuPlacement="auto"
        menuPortalTarget={
          typeof document !== "undefined" ? document.body : null
        }
        styles={{
          menuPortal: (base) => ({
            ...base,
            zIndex: 1600,
          }),
        }}
        onChange={(nextValue: MultiValue<ChoiceOption<T>>) => {
          onChange(nextValue.map((option) => option.value));
        }}
        classNames={multiSelectClassNames<ChoiceOption<T>>()}
      />
    </label>
  );
};

// How a typed value is normalized to the form capture stores, so a filter on
// "Example.com" or "uk-UA" matches instead of silently returning nothing.
// Mirrors apps/backend/src/common/utils/capture.ts (captureHost,
// captureLanguage) and the lower-cased os in rtb.capture.ts.
export type TextNormalize = "host" | "lower" | "language";

const normalizeHost = (text: string): string => {
  const withScheme = text.includes("://") ? text : `https://${text}`;
  try {
    return new URL(withScheme).hostname.toLowerCase();
  } catch {
    return text.toLowerCase();
  }
};

const NORMALIZERS: Record<TextNormalize, (text: string) => string> = {
  host: normalizeHost,
  lower: (text) => text.toLowerCase(),
  language: (text) => (text.split(/[-_]/)[0] ?? text).toLowerCase(),
};

// Splits typed or pasted text on newlines and commas, normalizes each part, and
// drops blanks and duplicates.
export const parseTextList = (
  raw: string,
  normalize?: TextNormalize,
): string[] => {
  const apply = normalize ? NORMALIZERS[normalize] : (text: string) => text;
  return [
    ...new Set(
      raw
        .split(/[\n,]/)
        .map((part) => part.trim())
        .filter((part) => part.length > 0)
        .map(apply)
        .filter((part) => part.length > 0),
    ),
  ];
};

type TextOption = { value: string; label: string };
const toOption = (text: string): TextOption => ({ value: text, label: text });

// Typed values for fields with no list to pick from (domains, bundles, ids).
// Each value becomes a chip stored straight in the condition.
export const TextListField = ({
  label,
  value,
  onChange,
  normalize,
  placeholder,
}: {
  label: string;
  value: string[];
  onChange: (nextValue: string[]) => void;
  normalize?: TextNormalize | undefined;
  placeholder?: string;
}): JSX.Element => (
  <label className="block space-y-2">
    <FieldLabel label={label} />
    <CreatableSelect<TextOption, true>
      isMulti
      unstyled
      components={{ DropdownIndicator: null }}
      formatCreateLabel={(raw) => `Add "${raw}"`}
      noOptionsMessage={() => "Type a value"}
      menuPlacement="auto"
      menuPortalTarget={typeof document !== "undefined" ? document.body : null}
      styles={{ menuPortal: (base) => ({ ...base, zIndex: 1600 }) }}
      value={value.map(toOption)}
      placeholder={placeholder ?? "Type a value and press Enter"}
      onChange={(next: MultiValue<TextOption>) => {
        onChange(next.map((option) => option.value));
      }}
      onCreateOption={(raw) => {
        onChange([...new Set([...value, ...parseTextList(raw, normalize)])]);
      }}
      classNames={multiSelectClassNames<TextOption>()}
    />
  </label>
);
